

import {XhrService} from "../../../../default/global/ts/xhr.service";

export class Configurator2D {
    constructor(configuratorElement = document.querySelector('.js-configurator')) {
        if (!configuratorElement) return;
        this.configuratorElement = configuratorElement;
        this.formElement = document.querySelector('#itemcard_order_button_form_std');
        this.editableAttributesWrapper = this.configuratorElement.querySelectorAll('.js-editable-attribute-wrapper');
        this.additionalItemsWrapper = this.configuratorElement.querySelectorAll('.js-additional-items-wrapper');
        this.buyButton = this.formElement.querySelector(".js-addToBasketItemcard");
        this.hasMandatory = document.getElementById("configuratorHasMandatory").value === "true";
        this.init();
    }

    init() {

        this.bindAddonEvents();
        this.recalculatePrice();
        this.initSliders(this.editableAttributesWrapper);
        this.formValidationListener();
        this.formSubmitListener();
        this.updateFavouriteButton();
        // Only the configurator itself may gate the button here: without a mandatory
        // attribute there is nothing to validate, so the server-rendered button state
        // (e.g. disabled due to availability) must stay untouched. The second condition
        // releases the button that the server disabled for the mandatory selection.
        if (this.hasMandatory || this.buyButton?.hasAttribute('disabled')) this.updateBuyButtonState();
        // formSubmitListener() disables the button on every click to avoid a double
        // add-to-basket. basket.js handles that click via AJAX (no page reload), so it
        // dispatches this event once the request settles and we re-validate here,
        // otherwise the button would stay disabled forever after the first add. Cancel
        // the pending disable first, in case the request settles faster than its 100ms
        // delay, which would otherwise re-disable the button right after we enable it.
        document.addEventListener('itemcardAddToBasketSettled', () => {
            clearTimeout(this.disableAfterSubmitTimeout);
            this.updateBuyButtonState();
        });
    }

    // Re-checks form validity and updates the button accordingly. Used both here
    // (after init, once a mandatory attribute may already be validly pre-selected)
    // and on every change (see formValidationListener).
    updateBuyButtonState() {
        if (this.formIsValid()) {
            this.enabledBuyButton();
        } else {
            this.disableBuyButton();
        }
    }

    formValidationListener() {
        this.configuratorElement.querySelectorAll("input, select").forEach(input => {
            input.addEventListener('change', () => {
                this.updateBuyButtonState();
                this.updateFavouriteButton();
            });
        });
    }

    updateFavouriteButton() {
        const itemId = this.formElement.querySelector('[name="item_id"]')?.value;
        if (!itemId) return;

        const customizeData = {};
        new FormData(this.formElement).forEach((val, key) => {
            if (key.startsWith('customize_') && val !== undefined && val !== 'undefined' && val !== '') {
                customizeData[key] = val;
            }
        });

        XhrService.generalAjaxCall('check_favourite_config',
            Object.assign({item_id: itemId}, customizeData),
            'POST', 'json'
        ).then(res => {
            const btn = document.querySelector(
                `.js-addToFavorites[data-id="${itemId}"], .js-removeFromFavorites[data-id="${itemId}"]`
            );
            if (!btn) return;
            const icon = btn.querySelector('.icon');
            if (res.isFavourite) {
                btn.classList.remove('js-addToFavorites');
                btn.classList.add('js-removeFromFavorites', 'active');
                if (icon) { icon.classList.remove('icon-favorites'); icon.classList.add('icon-favorites-filled'); }
            } else {
                btn.classList.remove('js-removeFromFavorites', 'active');
                btn.classList.add('js-addToFavorites');
                if (icon) { icon.classList.remove('icon-favorites-filled'); icon.classList.add('icon-favorites'); }
            }
        });
    }

    formIsValid(){
        const inputs = this.configuratorElement.querySelectorAll("input, select");
        let hasUndefinedValue = false;

        inputs.forEach(i => {
            // Check for string "undefined" or actual undefined value
            if (i.required && (i.value === "undefined" || typeof i.value === "undefined")) {
                hasUndefinedValue = true;
            }
        });

        // Firefox reports no valueMissing for radios that belong to the form through the
        // form attribute instead of being nested in it, so checkValidity() would accept
        // an untouched mandatory group. Check those groups here.
        const openGroup = [...inputs].some(i => i.required && i.type === "radio"
            && !this.configuratorElement.querySelector(`input[name="${i.name}"]:checked`));

        return !(hasUndefinedValue || openGroup || !this.formElement.checkValidity());
    }
    formSubmitListener() {
        this.buyButton.addEventListener('click', () => {
            if(this.formIsValid()) {
             clearTimeout(this.disableAfterSubmitTimeout);
             this.disableAfterSubmitTimeout = setTimeout(()=>{
                 this.disableBuyButton();
             },100);
            }
        });
    }

    disableBuyButton() {
        if (this.buyButton) {
            this.buyButton.setAttribute("disabled", "disabled");
        }
    }

    enabledBuyButton() {
        if (this.buyButton) {
            this.buyButton.removeAttribute("disabled");
        }
    }

    bindAddonEvents() {
        this.additionalItemsWrapper.forEach(wrapper => {
            const addOnItem = wrapper.querySelector('.js-additional-item');
            const addOnAttributeWrappers = wrapper.querySelectorAll('.js-addon-attribute-wrapper');

            addOnItem.addEventListener('change', () => {
                const selectedItemNo = addOnItem.value.split(';;')[0];

                addOnAttributeWrappers.forEach(attributeWrapper => {
                    const show = attributeWrapper.dataset.itemNo === selectedItemNo;
                    attributeWrapper.classList.toggle('hidden', !show);
                });

                this.recalculatePrice();
                this.initSliders(addOnAttributeWrappers);
            });
        });
    }

    recalculatePrice() {
        const addOnItems = document.querySelectorAll('.js-additional-item');
        const basePriceWrapper = document.querySelector('#itemcard_order_button_form_std .basePrice');
        const basePriceEl = basePriceWrapper?.querySelector('span[itemprop=price]');
        // Ticket 226351 / S26-14397: itemprop=price already contains the cheapest
        // mandatory add-on ("ab" price), so the live total has to start from
        // data-base-price - the bare item price - and add the picked options on top.
        // Otherwise the mandatory option would be counted twice.
        const bareBasePrice = parseFloat(basePriceWrapper?.dataset.basePrice);
        let sum = (!isNaN(bareBasePrice) ? bareBasePrice : parseFloat(basePriceEl?.getAttribute('content'))) || 0;

        let mandatoryStillOpen = false;

        addOnItems.forEach(item => {
            const isChosen = item.value !== 'undefined' && item.value !== '';
            if (!isChosen) {
                if (item.required) {
                    mandatoryStillOpen = true;
                }
                return;
            }
            const newPrice = parseFloat(item.selectedOptions[0]?.dataset.price);
            if (!isNaN(newPrice)) {
                sum += newPrice;
            }
        });

        // As long as a mandatory group is unanswered, keep advertising the server
        // rendered "ab" price instead of the bare base price the customer cannot buy.
        // The label comes from the text constants via data-from-label.
        const fromPrice = parseFloat(basePriceWrapper?.dataset.fromPrice);
        const fromLabel = basePriceWrapper?.dataset.fromLabel || '';
        const showFromPrice = mandatoryStillOpen && !isNaN(fromPrice);

        const formattedPrice = (showFromPrice ? fromPrice : sum).toLocaleString('de-DE', {
            style: "currency",
            currency: "EUR"
        });
        const priceText = showFromPrice && fromLabel !== ''
            ? `${fromLabel} ${formattedPrice}`
            : formattedPrice;

        if (basePriceWrapper?.lastChild) {
            basePriceWrapper.replaceChild(document.createTextNode(priceText), basePriceWrapper.lastChild);
        }
    }

    initSliders(sliderElements) {
        sliderElements.forEach(slider => {
            const label = slider.querySelector('.js-variantSelect-label');
            const inputs = slider.querySelectorAll('.js-variantSelect-input');

            if (inputs.length > 0 && label) {
                const selectedInput = [...inputs].find(input => input.checked);
                if(selectedInput){
                    label.innerHTML = selectedInput.dataset.description;
                }else{
                    label.innerHTML = GLOBALS.tc.pleaseChoose;
                }
            }

            inputs.forEach(input => {
                input.addEventListener('change', () => {
                    if (input.checked && label) {
                        label.innerHTML = input.dataset.description;
                    }
                    document.activeElement.blur();
                });
            });


        });
    }
}
